/**
 * TenantSessionEnforcementCard — Console super_admin: limite sessioni anti-condivisione.
 *
 * Due sezioni:
 *  1. DEFAULT PIATTAFORMA (platform_settings/session_enforcement): vale per
 *     tutti i tenant senza override. Policy decisa 19/07/2026: enforce.
 *  2. OVERRIDE DI QUESTO TENANT (tenants/{id}.config.session_enforcement):
 *     eccezioni per-tenant decise dal super utente — MAI hardcoded nel codice
 *     (es. Vivisolar = monitor). "Eredita" = rimuove l'override.
 *
 * Scrittura via updateDoc diretto (la console è solo super_admin, pattern
 * ExemptControl di TenantBillingTab). La CF registerSession legge la config
 * a ogni registrazione: le modifiche hanno effetto immediato sui prossimi login.
 *
 * Gli avvisi «account condiviso» vanno all'elenco UNICO di chi gestisce la piattaforma
 * (`platform_config/avvisi_piattaforma`, 05/10/2026): il vecchio campo «Email avvisi operatore»
 * (`alert_email`) non vale più ed è stato tolto da qui; l'elenco si mostra in sola lettura.
 *
 * Vedi: docs/01-ARCHITECTURE/SESSION-ENFORCEMENT-MODEL.md
 *
 * @since 2026-07-19
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { osservaDestinatariAvvisiPiattaforma } from '../services/avvisiPiattaformaService';

type Mode = 'off' | 'monitor' | 'enforce';

const MODE_LABEL: Record<Mode, string> = {
  off: 'Spento — nessun controllo',
  monitor: 'Monitor — rileva e avvisa, non disconnette',
  enforce: 'Enforce — limite attivo con disconnessione (kick)',
};

const INHERIT = '__inherit__';

interface PlatformCfg {
  mode: Mode;
  max_sessions_web: number;
  max_sessions_mobile: number;
  kick_alert_threshold: number;
  kick_alert_window_hours: number;
}

const PLATFORM_DEFAULTS: PlatformCfg = {
  mode: 'enforce',
  max_sessions_web: 1,
  max_sessions_mobile: 1,
  kick_alert_threshold: 4,
  kick_alert_window_hours: 24,
};

interface Props {
  tenant: { id: string; config?: { session_enforcement?: { mode?: Mode } } };
  onChange: () => void;
}

export const TenantSessionEnforcementCard: React.FC<Props> = ({ tenant, onChange }) => {
  const [platform, setPlatform] = useState<PlatformCfg | null>(null);
  const [tenantMode, setTenantMode] = useState<string>(INHERIT);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [destinatariAvvisi, setDestinatariAvvisi] = useState<string[] | undefined>(undefined);

  useEffect(
    () => osservaDestinatariAvvisiPiattaforma(setDestinatariAvvisi, () => setDestinatariAvvisi([])),
    [],
  );

  useEffect(() => {
    // Default piattaforma (doc unico)
    getDoc(doc(db, 'platform_settings', 'session_enforcement'))
      .then((snap) => {
        const d = (snap.data() ?? {}) as Partial<PlatformCfg>;
        setPlatform({ ...PLATFORM_DEFAULTS, ...d });
      })
      .catch(() => setPlatform(PLATFORM_DEFAULTS));
  }, []);

  useEffect(() => {
    const m = tenant.config?.session_enforcement?.mode;
    setTenantMode(m === 'off' || m === 'monitor' || m === 'enforce' ? m : INHERIT);
  }, [tenant.id, tenant.config?.session_enforcement?.mode]);

  const savePlatform = async (patch: Partial<PlatformCfg>) => {
    if (!platform) return;
    setSaving(true);
    setFeedback(null);
    try {
      const next = { ...platform, ...patch };
      await setDoc(doc(db, 'platform_settings', 'session_enforcement'), next, { merge: true });
      setPlatform(next);
      setFeedback({ severity: 'success', message: 'Default piattaforma aggiornato.' });
    } catch (err) {
      setFeedback({ severity: 'error', message: `Salvataggio fallito: ${String(err)}` });
    } finally {
      setSaving(false);
    }
  };

  const saveTenantOverride = async (value: string) => {
    setSaving(true);
    setFeedback(null);
    try {
      await updateDoc(doc(db, 'tenants', tenant.id), {
        'config.session_enforcement':
          value === INHERIT ? deleteField() : { mode: value as Mode },
      });
      setTenantMode(value);
      setFeedback({
        severity: 'success',
        message:
          value === INHERIT
            ? 'Override rimosso: il tenant segue il default piattaforma.'
            : `Override salvato: ${MODE_LABEL[value as Mode]}.`,
      });
      onChange();
    } catch (err) {
      setFeedback({ severity: 'error', message: `Salvataggio fallito: ${String(err)}` });
    } finally {
      setSaving(false);
    }
  };

  const effective: Mode = tenantMode === INHERIT ? (platform?.mode ?? 'enforce') : (tenantMode as Mode);

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <DevicesOtherIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            Limite sessioni (anti-condivisione account)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Un account = una sessione web + una app Android alla volta. Chi entra da un
          nuovo dispositivo sceglie se disconnettere l'altro (takeover); i pattern da
          account condiviso generano un avviso automatico all'operatore.
        </Typography>

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {/* ── Override di questo tenant ── */}
        <Typography variant="overline" color="text.secondary">
          Questo tenant
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <TextField
            select
            size="small"
            label="Modalità per questo tenant"
            value={tenantMode}
            onChange={(e) => saveTenantOverride(e.target.value)}
            disabled={saving}
            sx={{ minWidth: 340 }}
          >
            <MenuItem value={INHERIT}>
              Eredita default piattaforma{platform ? ` (${MODE_LABEL[platform.mode]})` : ''}
            </MenuItem>
            <MenuItem value="off">{MODE_LABEL.off}</MenuItem>
            <MenuItem value="monitor">{MODE_LABEL.monitor}</MenuItem>
            <MenuItem value="enforce">{MODE_LABEL.enforce}</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Effettiva: <strong>{MODE_LABEL[effective]}</strong>
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* ── Default piattaforma ── */}
        <Typography variant="overline" color="text.secondary">
          Default piattaforma (tutti i tenant senza override)
        </Typography>
        {!platform ? (
          <Skeleton height={56} />
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
            <TextField
              select
              size="small"
              label="Modalità default"
              value={platform.mode}
              onChange={(e) => savePlatform({ mode: e.target.value as Mode })}
              disabled={saving}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="off">{MODE_LABEL.off}</MenuItem>
              <MenuItem value="monitor">{MODE_LABEL.monitor}</MenuItem>
              <MenuItem value="enforce">{MODE_LABEL.enforce}</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Soglia avviso (kick)"
              value={platform.kick_alert_threshold}
              onChange={(e) => setPlatform({ ...platform, kick_alert_threshold: Number(e.target.value) || 1 })}
              sx={{ width: 160 }}
              inputProps={{ min: 1 }}
            />
            <TextField
              size="small"
              type="number"
              label="Finestra avviso (ore)"
              value={platform.kick_alert_window_hours}
              onChange={(e) => setPlatform({ ...platform, kick_alert_window_hours: Number(e.target.value) || 1 })}
              sx={{ width: 160 }}
              inputProps={{ min: 1 }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={saving}
              onClick={() => savePlatform({})}
              sx={{ minHeight: 40 }}
            >
              Salva default
            </Button>
          </Box>
        )}
        {destinatariAvvisi !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {destinatariAvvisi.length
              ? `Gli avvisi «account condiviso» vanno a chi gestisce la piattaforma: ${destinatariAvvisi.join(', ')} (elenco unico, platform_config/avvisi_piattaforma).`
              : '⚠️ Nessun destinatario per gli avvisi della piattaforma: platform_config/avvisi_piattaforma è vuoto.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
